import { Component } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import styles from './ErrorBoundary.module.css';

function DefaultErrorFallback({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className={styles.fallbackContainer} role="alert">
      <div className={styles.mascot} aria-hidden="true">🐼</div>
      <h1 className={styles.heading}>{t('errors.boundary')}</h1>
      <p className={styles.message}>
        {message || t('errors.boundaryDesc')}
      </p>
      <button className={styles.retryButton} onClick={onRetry}>
        {t('common.tryAgain')}
      </button>
    </div>
  );
}

function InlineErrorFallback({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className={styles.fallbackContainerInline} role="alert">
      <div className={styles.mascot} aria-hidden="true">🐼</div>
      <h2 className={styles.heading}>{t('errors.boundaryMobile')}</h2>
      <p className={styles.message}>
        {message || t('errors.boundaryMobileDesc')}
      </p>
      <button className={styles.retryButton} onClick={onRetry}>
        {t('common.tryAgain')}
      </button>
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // FP-OBSERVABILITY-01 (PR-16): Sentry's React SDK auto-captures unhandled
    // window errors but NOT errors intercepted by a boundary. With 20+ routes
    // wrapped in <ErrorBoundary>, these render errors are otherwise invisible
    // in production. Capture unconditionally; keep the DEV console.error.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });
    if (import.meta.env.DEV) console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          retry: this.handleRetry,
        });
      }
      return (
        <DefaultErrorFallback
          message={this.props.fallbackMessage}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

export { InlineErrorFallback };
export default withTranslation()(ErrorBoundary);
